import type { CustomerInfo, ShopData } from "../types";
import { validateCityState, validateEmail, validateZip } from "./validation";

type LookupCustomer = Partial<Omit<CustomerInfo, "address">> & {
  address1?: string;
  city?: string;
  state?: string;
  zip?: string;
};

type CustomerDraft = Pick<CustomerInfo, "fname" | "lname" | "email"> & {
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
};

type MissingRequiredCustomerField =
  | "fname"
  | "lname"
  | "email"
  | "street"
  | "city"
  | "state"
  | "zip";

type RequiredCustomerFlags = Pick<
  ShopData,
  "requireAddress" | "requireNewCustomerEmail"
>;

// A stored value only counts if it passes the same format check we apply to
// typed input. Anything else — a blank, a placeholder an advisor typed ("N/A",
// "none", a city with a stray digit, …), or an otherwise malformed value — is
// treated as no usable value, so the customer is prompted to supply a valid one.
// We validate the value rather than maintain a brittle list of placeholders.
const normalizeStored = (
  value: string | undefined,
  isValid: (value: string) => boolean
): string => {
  const trimmed = (value || "").trim();
  return isValid(trimmed) ? trimmed : "";
};

const normalizeStoredEmail = (value?: string): string =>
  normalizeStored(value, validateEmail);

// True when a stored email is present but not a valid address (placeholder,
// typo, etc.). An empty email returns false — absence is handled by the
// required-field rules, not here.
export const isInvalidEmail = (value?: string): boolean => {
  const email = (value || "").trim();
  return Boolean(email) && !validateEmail(email);
};

export const buildCustomerDraft = (
  lookupCustomer: LookupCustomer = {}
): CustomerDraft => ({
  fname: lookupCustomer?.fname || "",
  lname: lookupCustomer?.lname || "",
  email: normalizeStoredEmail(lookupCustomer?.email),
  address: {
    // street has no format rule, so it passes through as-is. city/state/zip are
    // structured data: a format-failing stored value is blanked so it surfaces
    // as a missing required field and is re-collected (when the shop requires an
    // address), rather than silently trusted like a name.
    street: lookupCustomer?.address1 || "",
    city: normalizeStored(lookupCustomer?.city, validateCityState),
    state: normalizeStored(lookupCustomer?.state, validateCityState),
    zip: normalizeStored(lookupCustomer?.zip, validateZip),
  },
});

export const getMissingRequiredCustomerFields = (
  customerDraft: Partial<CustomerDraft> = {},
  shopData: Partial<RequiredCustomerFlags> = {}
): MissingRequiredCustomerField[] => {
  const missingFields: MissingRequiredCustomerField[] = [];

  if (!customerDraft?.fname?.trim?.()) missingFields.push("fname");
  if (!customerDraft?.lname?.trim?.()) missingFields.push("lname");

  // buildCustomerDraft already blanks any non-valid stored email, so an empty
  // value here also covers placeholders/invalid addresses.
  if (shopData?.requireNewCustomerEmail && !customerDraft?.email?.trim?.()) {
    missingFields.push("email");
  }

  if (shopData?.requireAddress) {
    if (!customerDraft?.address?.street?.trim?.()) missingFields.push("street");
    if (!customerDraft?.address?.city?.trim?.()) missingFields.push("city");
    if (!customerDraft?.address?.state?.trim?.()) missingFields.push("state");
    if (!customerDraft?.address?.zip?.trim?.()) missingFields.push("zip");
  }

  return missingFields;
};
